import { useLocalStorage } from '@uidotdev/usehooks';
import React, { ReactNode, useCallback } from 'react';

import { Icon, Stack, Text, tv } from '@eluve/components';
import { useTranslation } from '@eluve/i18n';

export const DISMISSIBLE_CALLOUT_LOCAL_STORAGE_KEY =
  'dismissibleCalloutsDismissed';

const styles = tv({
  slots: {
    container:
      'group relative cursor-pointer rounded-lg border border-border-primary bg-bg-secondary px-3 pt-2 pb-4 text-text-tertiary',
    overlay:
      'absolute inset-0 flex items-center justify-center rounded-lg bg-bg-secondary/60 opacity-0 transition-opacity duration-200 group-hover:opacity-100 focus-visible:opacity-100 focus-visible:outline-none supports-backdrop-filter:backdrop-blur-sm',
  },
});

export type DismissibleCalloutProps = {
  id: string;
  title: string;
  description: ReactNode;
  className?: string;
};

export const DismissibleCallout: React.FC<DismissibleCalloutProps> = ({
  id,
  title,
  description,
  className,
}) => {
  const { t } = useTranslation();
  const [rawDismissedIds, setDismissedIds] = useLocalStorage<string[]>(
    DISMISSIBLE_CALLOUT_LOCAL_STORAGE_KEY,
    [],
  );
  const dismissedIds = Array.isArray(rawDismissedIds) ? rawDismissedIds : [];

  const handleDismiss = useCallback(() => {
    setDismissedIds((prev) => {
      const arr = Array.isArray(prev) ? prev : [];
      return arr.includes(id) ? arr : [...arr, id];
    });
  }, [id, setDismissedIds]);

  if (dismissedIds.includes(id)) {
    return null;
  }

  const { container, overlay } = styles();

  return (
    <Stack
      direction="vertical"
      gap={2}
      align="center"
      className={container({ className })}
    >
      <Icon name="Info" size="sm" />
      <Text weight="semibold" size="xs" color="inherit">
        {title}
      </Text>
      <Text className="text-center text-balance" color="inherit" size="xs">
        {description}
      </Text>
      <button
        type="button"
        className={overlay()}
        aria-label={t('blocks.callout.dismissTip', { title })}
        onClick={handleDismiss}
      >
        <Text weight="medium" size="xs">
          {t('blocks.callout.dontShowAgain')}
        </Text>
      </button>
    </Stack>
  );
};
